import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authApi } from '../api/authApi';
import { accountApi } from '../api/accountApi';
import { useAuth } from './AuthContext';

const RiskContext = createContext(null);
const STORAGE_KEY = 'lt-risk-settings';

export const DEFAULT_RISK_SETTINGS = {
  enabled: true,
  dailyLossLimitUsd: null,
  maxPositionNotionalUsd: null,
  maxPositionQty: null,
  warnOnPdt: true,
  warnOnBuyingPower: true,
  blockOnDailyLoss: true,
  blockOnMaxSize: true,
  /** Algo capital: max $ the algo runner may deploy per order / sizing */
  algoBudgetUsd: null,
  /** Max loss mode for algos: 'usd' | 'pct' */
  algoMaxLossMode: 'usd',
  algoMaxLossUsd: null,
  /** Percent when mode is pct (of equity or of budget) */
  algoMaxLossPct: null,
  /** Basis for pct max-loss: 'equity' | 'budget' */
  algoMaxLossBasis: 'equity',
  blockOnAlgoMaxLoss: true,
};

const loadLocal = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!raw || typeof raw !== 'object') return { ...DEFAULT_RISK_SETTINGS };
    return { ...DEFAULT_RISK_SETTINGS, ...raw };
  } catch {
    return { ...DEFAULT_RISK_SETTINGS };
  }
};

const persistLocal = (settings) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
};

export function computeDayPl(account) {
  if (!account) return null;
  const equity = Number(account.equity);
  const lastEquity = Number(account.lastEquity);
  if (Number.isFinite(equity) && Number.isFinite(lastEquity) && lastEquity > 0) {
    return equity - lastEquity;
  }
  return null;
}

/**
 * Resolve max-loss dollars from algo budget settings + account.
 */
export function resolveAlgoMaxLossUsd(settings, account = null) {
  const cfg = { ...DEFAULT_RISK_SETTINGS, ...(settings || {}) };
  const equity = Number(account?.equity) || 0;
  const budget =
    cfg.algoBudgetUsd != null && Number.isFinite(Number(cfg.algoBudgetUsd))
      ? Math.abs(Number(cfg.algoBudgetUsd))
      : null;

  if (cfg.algoMaxLossMode === 'pct') {
    const pct = Number(cfg.algoMaxLossPct);
    if (!Number.isFinite(pct) || pct <= 0) return null;
    const base =
      cfg.algoMaxLossBasis === 'budget' && budget != null && budget > 0 ? budget : equity;
    if (!base || base <= 0) return null;
    return base * (pct / 100);
  }

  const usd = Number(cfg.algoMaxLossUsd);
  if (!Number.isFinite(usd) || usd <= 0) return null;
  return Math.abs(usd);
}

/**
 * Evaluate an order intent against risk settings + live account snapshot.
 * @returns {{ ok: boolean, blocks: string[], warnings: string[], dayPl: number|null, account: object|null, estimatedNotional: number|null, algoMaxLossUsd: number|null }}
 */
export function evaluateOrderRisk({ settings, account, order }) {
  const blocks = [];
  const warnings = [];
  const cfg = { ...DEFAULT_RISK_SETTINGS, ...(settings || {}) };
  const dayPl = computeDayPl(account);
  const qty = order?.qty != null ? Number(order.qty) : null;
  const notional =
    order?.notional != null
      ? Number(order.notional)
      : order?.estimatedNotional != null
        ? Number(order.estimatedNotional)
        : null;
  const algoMaxLossUsd = resolveAlgoMaxLossUsd(cfg, account);
  const side = String(order?.side || '').toLowerCase();
  const isOpeningBuy = side === 'buy';

  if (!cfg.enabled) {
    return {
      ok: true,
      blocks,
      warnings,
      dayPl,
      account,
      estimatedNotional: notional,
      algoMaxLossUsd,
    };
  }

  if (account?.tradingBlocked || account?.accountBlocked) {
    blocks.push('Account trading is blocked');
  }

  if (
    cfg.blockOnDailyLoss &&
    cfg.dailyLossLimitUsd != null &&
    dayPl != null &&
    dayPl <= -Math.abs(cfg.dailyLossLimitUsd)
  ) {
    blocks.push(
      `Daily P/L limit hit (${dayPl.toFixed(2)} ≤ -${Number(cfg.dailyLossLimitUsd).toFixed(2)})`
    );
  }

  if (
    cfg.blockOnAlgoMaxLoss &&
    algoMaxLossUsd != null &&
    dayPl != null &&
    dayPl <= -Math.abs(algoMaxLossUsd) &&
    isOpeningBuy
  ) {
    blocks.push(
      `Algo max-loss hit (today P/L ${dayPl.toFixed(2)} ≤ -$${algoMaxLossUsd.toFixed(2)}) — new buys blocked`
    );
  }

  if (cfg.blockOnMaxSize && cfg.maxPositionQty != null && qty != null && Number.isFinite(qty)) {
    if (qty > cfg.maxPositionQty) {
      blocks.push(
        `Quantity ${qty} exceeds max position size (${cfg.maxPositionQty} shares)`
      );
    }
  }

  const effectiveNotionalCap = (() => {
    const caps = [];
    if (cfg.maxPositionNotionalUsd != null) caps.push(Number(cfg.maxPositionNotionalUsd));
    if (cfg.algoBudgetUsd != null) caps.push(Number(cfg.algoBudgetUsd));
    const finite = caps.filter((n) => Number.isFinite(n) && n > 0);
    return finite.length ? Math.min(...finite) : null;
  })();

  if (
    cfg.blockOnMaxSize &&
    effectiveNotionalCap != null &&
    notional != null &&
    Number.isFinite(notional) &&
    isOpeningBuy
  ) {
    if (notional > effectiveNotionalCap) {
      blocks.push(
        `Order notional $${notional.toFixed(2)} exceeds budget/cap $${effectiveNotionalCap.toFixed(2)}`
      );
    }
  }

  if (cfg.warnOnPdt && account) {
    const equity = Number(account.equity) || 0;
    const dayTradeCount = Number(account.dayTradeCount) || 0;
    if (account.patternDayTrader) {
      warnings.push('Account is flagged Pattern Day Trader (PDT)');
    } else if (equity > 0 && equity < 25000 && dayTradeCount >= 3) {
      warnings.push(
        `Near/at PDT threshold: ${dayTradeCount} day trades with equity under $25,000`
      );
    } else if (equity > 0 && equity < 25000 && dayTradeCount >= 2) {
      warnings.push(
        `${dayTradeCount} day trades today/period — one more may trigger PDT under $25k`
      );
    }
  }

  if (cfg.warnOnBuyingPower && account && notional != null && Number.isFinite(notional) && order?.side === 'buy') {
    const bp = Number(account.buyingPower) || 0;
    const dtbp = Number(account.dayTradingBuyingPower) || 0;
    const available = Math.max(bp, dtbp);
    if (available > 0 && notional > available) {
      warnings.push(
        `Estimated notional $${notional.toFixed(2)} exceeds buying power $${available.toFixed(2)}`
      );
    } else if (bp > 0 && notional > bp * 0.9) {
      warnings.push(
        `Order uses ${((notional / bp) * 100).toFixed(0)}% of buying power ($${bp.toFixed(2)})`
      );
    }
  }

  if (cfg.algoBudgetUsd != null && Number(cfg.algoBudgetUsd) > 0) {
    warnings.push(`Algo budget active: $${Number(cfg.algoBudgetUsd).toFixed(2)}`);
  }

  return {
    ok: blocks.length === 0,
    blocks,
    warnings,
    dayPl,
    account,
    estimatedNotional: notional,
    algoMaxLossUsd,
  };
}

export const RiskProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState(() => loadLocal());
  const [hydrated, setHydrated] = useState(false);
  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setHydrated(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await authApi.getPreferences();
        const remote = response.data?.riskSettings;
        if (!cancelled && remote && typeof remote === 'object') {
          const merged = { ...DEFAULT_RISK_SETTINGS, ...remote };
          setSettings(merged);
          persistLocal(merged);
        }
      } catch {
        // keep local
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    persistLocal(settings);
    if (!hydrated || !isAuthenticated) return undefined;
    const timer = setTimeout(() => {
      authApi.savePreferences({ riskSettings: settings }).catch(() => {});
    }, 450);
    return () => clearTimeout(timer);
  }, [settings, hydrated, isAuthenticated]);

  const refreshAccount = useCallback(async () => {
    if (!isAuthenticated) {
      setAccount(null);
      return null;
    }
    try {
      setAccountLoading(true);
      const response = await accountApi.getAccount();
      const data = response.data || response;
      setAccount(data);
      return data;
    } catch (err) {
      console.error('Risk account refresh failed:', err);
      return null;
    } finally {
      setAccountLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setAccount(null);
      return undefined;
    }
    refreshAccount();
    const interval = setInterval(refreshAccount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refreshAccount]);

  const updateSettings = useCallback((patch) => {
    setSettings((prev) => ({ ...prev, ...patch }));
  }, []);

  const checkOrder = useCallback(
    async (order) => {
      const live = (await refreshAccount()) || account;
      return evaluateOrderRisk({ settings, account: live, order });
    },
    [refreshAccount, account, settings]
  );

  const dayPl = useMemo(() => computeDayPl(account), [account]);
  const algoMaxLossUsd = useMemo(
    () => resolveAlgoMaxLossUsd(settings, account),
    [settings, account]
  );

  const value = useMemo(
    () => ({
      settings,
      updateSettings,
      account,
      accountLoading,
      refreshAccount,
      dayPl,
      algoMaxLossUsd,
      checkOrder,
      evaluate: (order) => evaluateOrderRisk({ settings, account, order }),
    }),
    [settings, updateSettings, account, accountLoading, refreshAccount, dayPl, algoMaxLossUsd, checkOrder]
  );

  return <RiskContext.Provider value={value}>{children}</RiskContext.Provider>;
};

export const useRisk = () => {
  const ctx = useContext(RiskContext);
  if (!ctx) throw new Error('useRisk must be used within RiskProvider');
  return ctx;
};
